import {
    computed,
    Directive,
    effect,
    ElementRef,
    inject,
    input,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import {
    resolveColorContext,
    type ContextFamily,
    type ContextTone,
} from '@quipex/shared/utils';
import { QpxButton } from '../button/button';

export type IconColor = ContextFamily;

/** map of alias to class */
export const FONT_SET_MAP = {
    ['sym-outlined']: 'material-symbols-outlined',
} as const;

export type IconSetAlias = keyof typeof FONT_SET_MAP;

const MAT_ICON_CLASS_TRIGGER_ATTRS = [
    'matPrefix',
    'matSuffix',
    'matIconPrefix',
    'matIconSuffix',
    'matButtonIcon',
    'matMenuItemIcon',
] as const;

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'mat-icon[qpx], ng-icon[qpx]',
    host: {
        '[class]': 'className()',
        '[style.--mat-icon-color]': 'contextColor()',
    },
})
export class QpxIcon {
    //#region Dependency injection
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef, {
        host: true,
    });
    private readonly qpxButtonRef = inject(QpxButton, {
        optional: true,
        skipSelf: true,
    });
    private readonly matIconRef = inject(MatIcon, {
        host: true,
        optional: true,
    });
    //#endregion

    //#region Inputs
    /** Controls rendered icon scale in UI contexts (`xs`..`lg`). */
    public readonly size = input<'lg' | 'md' | 'sm' | 'xs'>();

    /** Selects Material font-set alias for `mat-icon` hosts. */
    public readonly set = input<IconSetAlias>('sym-outlined');

    /** Applies semantic color context to icon foreground. */
    public readonly context = input<IconColor>();
    public readonly tone = input<ContextTone>('default');

    /** Chooses Material icon style class when host is `mat-icon`. */
    public readonly type = input<'outlined' | 'filled'>('outlined');
    //#endregion

    //#region State
    private readonly resolvedSize = computed(
        () => this.size() ?? this.qpxButtonRef?.size() ?? 'md'
    );

    protected readonly contextColor = computed(() => {
        const context = this.context();
        return context
            ? `var(${resolveColorContext(context, this.tone()).decorative.variable})`
            : null;
    });

    protected readonly className = computed(() => {
        const _className = ['qpx-icon', this.resolvedSize()];
        const context = this.context();
        const hasMatIconClassTrigger = MAT_ICON_CLASS_TRIGGER_ATTRS.some(
            (attr) => this.elementRef.nativeElement.hasAttribute(attr)
        );
        const hasMatMenuItemIcon =
            this.elementRef.nativeElement.hasAttribute('matMenuItemIcon');

        if (hasMatIconClassTrigger) {
            _className.push('mat-icon');
        }

        if (hasMatMenuItemIcon && !context) {
            _className.push('mat-icon-no-color');
        }

        if (this.matIconRef) {
            if (this.type() === 'filled') {
                _className.push('icon-filled');
            } else {
                _className.push('icon-outlined');
            }
        }

        if (context) {
            _className.push(
                resolveColorContext(context, this.tone()).decorative.className
            );
        }

        return _className.join(' ');
    });
    //#endregion

    //#region Lifecycle hooks
    constructor() {
        effect(() => {
            const set = this.set();

            if (!this.matIconRef || !set) {
                return;
            }

            const fontSet = FONT_SET_MAP[set];

            if (fontSet) {
                this.matIconRef.fontSet = fontSet;
            }
        });
    }
    //#endregion
}
